<template>
    <pendo-star-cell
        :class="{ 'pendo-star--disabled': disabled }"
        :row="{ starred: isStarredById(id) }"
        action-type="action"
        :size="buttonSize"
        :type="buttonType"
        @star-change="toggleStarred" />
</template>

<script>
import { PendoNotification, PendoStarCell } from '@pendo/components-vue3';
import { mapActions, mapGetters } from 'vuex';
import useAriaLiveRegistry from '@/common/use/use-aria-live-registry/use-aria-live-registry';

export default {
    name: 'Star',
    components: {
        PendoStarCell,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            required: true,
            validator: (value) => {
                return [
                    'Page',
                    'Feature',
                    'Guide',
                    'TrackType',
                    'Visitor',
                    'Account',
                    'Dashboard',
                    'Segment',
                    'SessionRecording',
                    'Report',
                    'Workflow',
                    'Roadmap',
                    'SavedView',
                    'Survey',
                    'Recommendation',
                    'TrackedUseCase',
                    'TrackedIssue',
                ].includes(value);
            },
        },
        buttonSize: {
            type: String,
            default: 'mini',
        },
        buttonType: {
            type: String,
            default: 'tertiary',
        },
    },
    emits: ['star-added', 'star-removed'],
    setup () {
        const { queueMessage } = useAriaLiveRegistry();

        return { queueMessage };
    },
    computed: {
        ...mapGetters({
            isStarredById: 'stars/isStarredById',
        }),
    },
    methods: {
        ...mapActions({
            addStar: 'stars/addNow',
            removeStar: 'stars/removeNow',
        }),
        async toggleStarred ({ value }) {
            if (value) {
                try {
                    await this.addStar({ id: this.id, type: this.type });
                    this.queueMessage({
                        message: this.$t('star.Added_to_favorites'),
                        ariaLiveMode: 'polite',
                    });
                    this.$emit('star-added', { id: this.id });
                } catch (error) {
                    PendoNotification({
                        type: 'error',
                        title: 'Something went wrong',
                        message: 'There was an issue adding this item to your favorites. Please try again.',
                    });
                    this.queueMessage({
                        message: this.$t('star.Failed_to_update_favorites'),
                        ariaLiveMode: 'assertive',
                        highPriority: true,
                    });
                    throw error;
                }
            } else {
                try {
                    await this.removeStar({ id: this.id, type: this.type });
                    this.queueMessage({
                        message: this.$t('star.Removed_from_favorites'),
                        ariaLiveMode: 'polite',
                    });
                    this.$emit('star-removed', { id: this.id });
                } catch (error) {
                    PendoNotification({
                        type: 'error',
                        title: 'Something went wrong',
                        message: 'There was an issue removing this item from your favorites. Please try again.',
                    });
                    this.queueMessage({
                        message: this.$t('star.Failed_to_update_favorites'),
                        ariaLiveMode: 'assertive',
                        highPriority: true,
                    });
                    throw error;
                }
            }
        },
    },
};
</script>

<style scoped>
.pendo-star--disabled {
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.6;
}
</style>
